/**
 * PWA install prompt hook + button.
 *
 * Chromium browsers fire `beforeinstallprompt` when the site meets PWA criteria.
 * We capture and defer it, then show an "Install ForgeOS" button that resolves
 * the saved event. On iOS Safari (no beforeinstallprompt) we detect standalone
 * mode and show a small instructional tooltip.
 */
import React, { useEffect, useState, useCallback } from "react";
import { Download, Smartphone, X } from "lucide-react";

function isIOS() {
    if (typeof navigator === "undefined") return false;
    return /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
}
function isStandalone() {
    if (typeof window === "undefined") return false;
    return window.matchMedia?.("(display-mode: standalone)").matches
        || window.navigator.standalone === true;
}

export function usePwaInstall() {
    const [prompt, setPrompt] = useState(null);
    const [installed, setInstalled] = useState(isStandalone());

    useEffect(() => {
        function onBip(e) {
            e.preventDefault();
            setPrompt(e);
        }
        function onInstalled() {
            setPrompt(null);
            setInstalled(true);
        }
        window.addEventListener("beforeinstallprompt", onBip);
        window.addEventListener("appinstalled", onInstalled);
        return () => {
            window.removeEventListener("beforeinstallprompt", onBip);
            window.removeEventListener("appinstalled", onInstalled);
        };
    }, []);

    const trigger = useCallback(async () => {
        if (!prompt) return null;
        prompt.prompt();
        const { outcome } = await prompt.userChoice;
        setPrompt(null);
        return outcome; // "accepted" | "dismissed"
    }, [prompt]);

    return { canInstall: !!prompt, installed, trigger, isIOS: isIOS() };
}

export default function InstallForgeOSButton({ testid = "install-forgeos" }) {
    const { canInstall, installed, trigger, isIOS: ios } = usePwaInstall();
    const [showIOS, setShowIOS] = useState(false);

    if (installed) return null;

    if (!canInstall && !ios) return null;

    return (
        <>
            <button
                onClick={async () => {
                    if (canInstall) {
                        await trigger();
                    } else if (ios) {
                        setShowIOS(true);
                    }
                }}
                data-testid={testid}
                className="flex items-center gap-2 text-xs px-3 py-1.5 border border-border hover:bg-secondary/50 transition-colors"
            >
                {ios ? <Smartphone size={12} /> : <Download size={12} />}
                <span>Install ForgeOS</span>
            </button>

            {showIOS && (
                <div className="fixed inset-0 z-50 bg-black/60 flex items-end md:items-center justify-center p-4" onClick={() => setShowIOS(false)}>
                    <div className="bg-background border border-border max-w-md w-full p-6" onClick={(e) => e.stopPropagation()} data-testid="install-ios-modal">
                        <div className="flex items-start justify-between mb-3">
                            <div>
                                <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Install on iOS</div>
                                <h3 className="text-xl mt-1" style={{ fontFamily: "'Cormorant Garamond', serif" }}>Add ForgeOS to Home Screen</h3>
                            </div>
                            <button onClick={() => setShowIOS(false)} className="p-1"><X size={14} /></button>
                        </div>
                        <ol className="text-sm space-y-2 mt-4 list-decimal pl-4">
                            <li>Open this page in <b>Safari</b> (not Chrome).</li>
                            <li>Tap the <b>Share</b> icon (square with an arrow).</li>
                            <li>Scroll and choose <b>Add to Home Screen</b>.</li>
                            <li>Tap <b>Add</b>. ForgeOS will appear on your home screen like a native app.</li>
                        </ol>
                    </div>
                </div>
            )}
        </>
    );
}
